import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Search, Users, Trash2, Edit3, Mail, Phone, MapPin } from "lucide-react";
import ModuleStaffTab from "@/components/staff/ModuleStaffTab";

const formatEur = (n) =>
  new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR" }).format(Number(n || 0));

export default function CRMDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/customers"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Poistetaanko asiakas?")) return;
    try { await api.delete(`/customers/${id}`); toast.success("Asiakas poistettu"); load(); }
    catch (e2) { toast.error(formatApiError(e2)); }
  };

  const filtered = items.filter((c) => {
    const s = q.toLowerCase();
    return !s || (c.name || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s) || (c.business_id || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-amber-400/70 mb-2">Sovellus</p>
          <h1 className="text-3xl font-display gold-text">CRM · Asiakkaat</h1>
          <p className="text-muted-foreground mt-1">Hallinnoi asiakassuhteita ja seuraa laskutusta.</p>
        </div>
        <Link to="/crm/new">
          <Button data-testid="crm-new-btn" className="bg-amber-500 text-zinc-900 hover:bg-amber-400 font-medium">
            <Plus className="w-4 h-4 mr-2" /> Uusi asiakas
          </Button>
        </Link>
      </header>

      <Tabs defaultValue="customers" className="space-y-4">
        <TabsList className="bg-white/[0.04] border border-white/5">
          <TabsTrigger value="customers" data-testid="crm-tab-customers">Asiakkaat</TabsTrigger>
          <TabsTrigger value="staff" data-testid="crm-tab-staff"><Users className="w-3.5 h-3.5 mr-1.5" />Henkilökunta</TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="space-y-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Hae nimellä, emaililla tai Y-tunnuksella..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="crm-search" className="pl-9 bg-input/40" />
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Ladataan...</div>
          ) : filtered.length === 0 ? (
            <Card className="panel-3d border-0">
              <CardContent className="relative z-10 text-center py-12">
                <Users className="w-12 h-12 mx-auto text-amber-500/30 mb-3" />
                <p className="text-muted-foreground mb-4">Ei asiakkaita vielä</p>
                <Link to="/crm/new">
                  <Button className="bg-amber-500 text-zinc-900 hover:bg-amber-400">Luo ensimmäinen asiakas</Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((c) => (
                <div
                  key={c.id}
                  onClick={() => navigate(`/crm/${c.id}`)}
                  data-testid={`customer-card-${c.id}`}
                  className="panel-3d-sm rounded-xl p-5 cursor-pointer hover:scale-[1.02] transition-transform relative z-10"
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <h3 className="font-display text-lg gold-text truncate">{c.name}</h3>
                      {c.business_id && <p className="text-xs text-muted-foreground font-mono">{c.business_id}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); navigate(`/crm/${c.id}`); }}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`customer-delete-${c.id}`} onClick={(e) => remove(c.id, e)}>
                        <Trash2 className="w-3.5 h-3.5 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  <div className="space-y-1.5 text-xs text-muted-foreground">
                    {c.email && <p className="flex items-center gap-2"><Mail className="w-3 h-3" /> {c.email}</p>}
                    {c.phone && <p className="flex items-center gap-2"><Phone className="w-3 h-3" /> {c.phone}</p>}
                    {(c.address || c.city) && <p className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {[c.address, c.city].filter(Boolean).join(", ")}</p>}
                  </div>
                  <div className="mt-4 pt-3 border-t border-amber-500/20 flex justify-between text-xs">
                    <div>
                      <p className="text-muted-foreground">Laskutettu</p>
                      <p className="font-semibold gold-text">{formatEur(c.total_invoiced)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-muted-foreground">Laskuja</p>
                      <p className="font-semibold text-amber-300">{c.invoices_count}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="staff">
          <ModuleStaffTab moduleKey="crm" moduleLabel="CRM · Asiakkaat" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
